import React, { useCallback, useState } from 'react';
import innerText from 'react-innertext';

import * as styles from './styles';

import { fonts } from '@cobliteam/base-components/src/styles';
import { CaptionBold } from '@cobliteam/base-components/src/typography';

import type { AlertConfigCTA } from '../AlertItem';

type MessageType = string | JSX.Element;

export type AlertMessage = MessageType | MessageType[];

export interface AlertMessageContentProps {
  message: AlertMessage;
  messageSize?: number;
  cta?: AlertConfigCTA;
}

const MESSAGE_MAX_WIDTH = 294;
export const MESSAGE_CHAR_LIMIT_WITHOUT_CTA = 80;
export const MESSAGE_CHAR_LIMIT_WITH_CTA = 65;

export const AlertMessageContent: React.FC<AlertMessageContentProps> =
  React.memo(({ message, messageSize, cta }: any) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const getMessageDetails = useCallback(() => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const messageTextContent = innerText(message);

      if (!ctx) {
        return { lines: 1, messageTextContent };
      }

      ctx.font = `${fonts.weight.bold} normal ${fonts.size.smallest} 'Roboto', sans-serif`;

      const width = Math.ceil(ctx.measureText(messageTextContent).width);
      const messageWidth = cta
        ? MESSAGE_MAX_WIDTH - Math.ceil(ctx.measureText(cta.text).width + 30)
        : MESSAGE_MAX_WIDTH;
      const lines = Math.ceil(width / messageWidth);

      return { lines, messageTextContent };
    }, [message]);

    const handleExpand = () => setIsExpanded((prev) => !prev);

    const formatMessageLength = useCallback(() => {
      const { lines, messageTextContent } = getMessageDetails();

      if (React.isValidElement(message)) {
        return message;
      }

      if (lines > 2) {
        if (isExpanded) {
          return (
            <>
              {`${message} `}
              <span className={styles.showMoreMessage} onClick={handleExpand}>
                Ver menos
              </span>
            </>
          );
        }
        const charLimit = cta
          ? MESSAGE_CHAR_LIMIT_WITH_CTA
          : MESSAGE_CHAR_LIMIT_WITHOUT_CTA;

        return (
          <>
            {messageTextContent.substring(0, charLimit)}
            {'... '}
            <span className={styles.showMoreMessage} onClick={handleExpand}>
              Ver mais
            </span>
          </>
        );
      }

      return message;
    }, [message, isExpanded, cta]);

    return (
      <CaptionBold
        className={styles.alertMessage(messageSize || MESSAGE_MAX_WIDTH)}
        id='alert-message'
      >
        {formatMessageLength()}
      </CaptionBold>
    );
  });
